import { Session } from "../types";

/**
 * The most recently touched session a user has against one scenario, or
 * undefined when they have never played it.
 *
 * "Most recent" is by `lastModifiedAt`, not creation order: a player who
 * starts a scenario, wanders off to another, and comes back expects to land
 * where they last spoke. Ties fall back to `id` so the choice is stable
 * across renders rather than dependent on the order the list arrived in.
 *
 * Shared by the library card, the briefing page and the app-level resume
 * handler, which must all agree on which session "resume" means — three
 * copies of this sort would be three chances to send the same click to
 * different places.
 */
export function latestSessionForScenario(
  sessions: readonly Session[],
  scenarioId: string,
): Session | undefined {
  return sessions
    .filter((s) => s.scenarioId === scenarioId)
    .reduce<Session | undefined>((latest, s) => {
      if (!latest) return s;
      const d = new Date(s.lastModifiedAt).getTime();
      const dLatest = new Date(latest.lastModifiedAt).getTime();
      if (d > dLatest) return s;
      if (d === dLatest && s.id > latest.id) return s;
      return latest;
    }, undefined);
}

/** Whether the user has any session at all against this scenario. */
export function hasSessionForScenario(
  sessions: readonly Session[],
  scenarioId: string,
): boolean {
  return sessions.some((s) => s.scenarioId === scenarioId);
}
